import React, { useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import "rsuite/dist/rsuite.min.css";
import Disclaimer from "./Disclaimer";
import { desclaimerMessage } from "../../utils/utils";
import {
  ConditionToggleButtons,
  BookingWindowSection,
  StayDatesSection,
  BlackoutDatesSection,
  LeadTimeSection,
  LengthOfStaySection,
} from "./TargetingConditions/index";
import { useTargetingConditionsState } from "./hooks/useTargetingConditionsState";

const TargetingConditions = forwardRef(function TargetingConditions(
  {
    promo,
    handleFieldChange,
    tc,
    setTc,
    setIsNextBtnDisabled,
    isPilotHotel = false,
    isEdit = false,
    isDuplicate = false,
  },
  ref
) {
  // HTT-9780: only an existing promo splits Booking/Stay into Start + End fields.
  // A duplicate is saved as a brand new promo, so it keeps the creation UI.
  const splitDateFields = isEdit && !isDuplicate;

  const {
    // Booking window
    fromBooking,
    toBooking,
    setShowBooking,
    handleBookingChange,

    // Stay dates (EO-1492 multi-range)
    stayDateRanges,
    setShowPSD,
    handleStayDateRangeChange,
    addStayDateRange,
    removeStayDateRange,
    stayDateRangeErrors,

    // Blackout dates
    blackoutDates,
    setShowBlackouts,
    setBODates,
    addBlackoutDate,
    removeBlackoutDate,
    handleAllowStayThrough,

    // Lead time
    ltType,
    setLTType,
    minBookingLeadTime,
    maxBookingLeadTime,
    setShowLT,
    setLTMinMax,
    setLTRange,

    // Length of stay
    losType,
    setLOSType,
    minLos,
    maxLos,
    setShowLOS,
    setLosMinMax,
    setLosRange,

    // Validation
    validateTargetingConditions,
    hasLosError,
    hasLeadTimeError,
    hasBookingDateError,
  } = useTargetingConditionsState({
    promo,
    handleFieldChange,
    tc,
    setTc,
    setIsNextBtnDisabled,
    isEditingExistingPromo: splitDateFields,
  });

  // Expose validation function to parent via ref
  useImperativeHandle(ref, () => ({
    validateTargetingConditions,
  }));

  return (
    <div className="targetting-conditions">
      {!isPilotHotel && (
        <Disclaimer DisclaimerMessage={desclaimerMessage}></Disclaimer>
      )}

      {/* Condition Sections - shown when buttons are clicked */}
      {tc.showBooking && (
        <BookingWindowSection
          fromBooking={fromBooking}
          toBooking={toBooking}
          onBookingChange={handleBookingChange}
          onRemove={setShowBooking}
          hasError={hasBookingDateError}
          splitDateFields={splitDateFields}
        />
      )}

      {tc.showPSD && (
        <StayDatesSection
          stayDateRanges={stayDateRanges}
          onStayDateRangeChange={handleStayDateRangeChange}
          onRemoveStayDateRange={removeStayDateRange}
          onAddStayDateRange={addStayDateRange}
          onRemoveSection={setShowPSD}
          hasErrors={stayDateRangeErrors}
          splitDateFields={splitDateFields}
        />
      )}

      {tc.showBlackouts && (
        <BlackoutDatesSection
          blackoutDates={blackoutDates}
          onBlackoutDateChange={setBODates}
          onRemoveBlackoutDate={removeBlackoutDate}
          onAddBlackoutDate={addBlackoutDate}
          onToggleAllowStayThrough={handleAllowStayThrough}
        />
      )}

      {tc.showLOS && (
        <LengthOfStaySection
          losType={losType}
          minLengthOfStay={minLos}
          maxLengthOfStay={maxLos}
          onLosTypeChange={setLOSType}
          onMinMaxChange={setLosMinMax}
          onRangeChange={setLosRange}
          onRemove={setShowLOS}
          hasError={hasLosError}
        />
      )}

      {tc.showLT && (
        <LeadTimeSection
          ltType={ltType}
          minBookingLeadTime={minBookingLeadTime}
          maxBookingLeadTime={maxBookingLeadTime}
          onLtTypeChange={setLTType}
          onMinMaxChange={setLTMinMax}
          onRangeChange={setLTRange}
          onRemove={setShowLT}
          hasError={hasLeadTimeError}
        />
      )}

      {/* Condition Buttons Section - Show all buttons, hide when condition is active */}
      <ConditionToggleButtons
        showPSD={tc.showPSD}
        showBooking={tc.showBooking}
        showBlackouts={tc.showBlackouts}
        showLOS={tc.showLOS}
        showLT={tc.showLT}
        onTogglePSD={setShowPSD}
        onToggleBooking={setShowBooking}
        onToggleBlackouts={setShowBlackouts}
        onToggleLOS={setShowLOS}
        onToggleLT={setShowLT}
      />
    </div>
  );
});

TargetingConditions.propTypes = {
  promo: PropTypes.shape({
    rateRule: PropTypes.shape({
      ruleVO: PropTypes.shape({
        bookingVO: PropTypes.shape({
          bookingStartDate: PropTypes.string,
          bookingEndDate: PropTypes.string,
          minBookingLeadTime: PropTypes.number,
          maxBookingLeadTime: PropTypes.number,
        }),
        stayVO: PropTypes.shape({
          stayStartDate: PropTypes.string,
          stayEndDate: PropTypes.string,
          blackOutDateRanges: PropTypes.arrayOf(
            PropTypes.shape({
              blackOutStartDate: PropTypes.string,
              blackOutEndDate: PropTypes.string,
              blackOutStayThruEnabled: PropTypes.bool,
            })
          ),
          minLengthOfStay: PropTypes.number,
          maxLengthOfStay: PropTypes.number,
        }),
      }),
    }),
  }),
  handleFieldChange: PropTypes.func.isRequired,
  tc: PropTypes.shape({
    showBooking: PropTypes.bool,
    showPSD: PropTypes.bool,
    showBlackouts: PropTypes.bool,
    showLT: PropTypes.bool,
    showLOS: PropTypes.bool,
  }).isRequired,
  setTc: PropTypes.func.isRequired,
  setIsNextBtnDisabled: PropTypes.func.isRequired,
  isPilotHotel: PropTypes.bool,
  isEdit: PropTypes.bool,
  isDuplicate: PropTypes.bool,
};

export default TargetingConditions;
